import React, { useState, useMemo, useEffect } from 'react';
import { THEME } from '../../styles/theme';
import {
  CONFLUENCE_FACTORS, GRADE_CONFIG,
  calculateConfluence, autoLoadFromStorage, getSetupContext,
} from '../../engines/confluenceEngine';
import { getCurrentPrice } from '../../services/storage';

// ── Styles ──────────────────────────────────────────────────────────────
const S = {
  page:  { padding: '20px', maxWidth: '100%', paddingBottom: '40px' },
  title: { fontSize: '22px', fontWeight: '700', color: THEME.colors.blue, letterSpacing: '2px', fontFamily: THEME.fonts.hud, marginBottom: '4px' },
  sub:   { fontSize: '13px', color: THEME.colors.textMuted, marginBottom: '20px' },
  card:  (border) => ({ background: 'rgba(255,255,255,0.03)', border: `1px solid ${border || 'rgba(255,255,255,0.07)'}`, borderRadius: '12px', padding: '16px', marginBottom: '14px' }),
  badge: (color, bg) => ({ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', color, background: bg, display: 'inline-block', fontFamily: THEME.fonts.hud }),
  sectionTitle: { fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.textMuted, fontFamily: THEME.fonts.hud, marginBottom: '10px', textTransform: 'uppercase' },
  btn: (v) => ({
    padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: '700', fontFamily: THEME.fonts.hud,
    background: v === 'primary' ? THEME.colors.blue : v === 'green' ? THEME.colors.green : v === 'danger' ? 'rgba(255,51,51,0.15)' : 'rgba(255,255,255,0.07)',
    color: v === 'primary' ? '#000' : v === 'green' ? '#000' : v === 'danger' ? THEME.colors.red : THEME.colors.textMuted,
  }),
};

// ── Grade Display ───────────────────────────────────────────────────────
function GradeCard({ result }) {
  if (!result) return null;
  const { grade, score, config, upgradeHint, factorCount, totalFactors } = result;
  const pct = Math.round((score / 10) * 100);

  return (
    <div style={{ ...S.card(config.color + '55'), padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        {/* Grade badge */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '56px', fontWeight: '900', color: config.color, fontFamily: THEME.fonts.hud, lineHeight: 1 }}>
            {config.emoji} {grade}
          </div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginTop: '4px' }}>SETUP GRADE</div>
        </div>

        {/* Score + bar */}
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '14px', color: THEME.colors.textMuted }}>Confluence Score</span>
            <span style={{ fontSize: '20px', fontWeight: '800', color: config.color, fontFamily: THEME.fonts.hud }}>{score}/10</span>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: config.color, borderRadius: '6px', transition: 'width 0.5s ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{factorCount}/{totalFactors} factors confirmed</span>
            <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>Expected Win Rate: {config.winRate}</span>
          </div>
        </div>

        {/* Action */}
        <div style={{ textAlign: 'center', minWidth: '160px' }}>
          <div style={{
            padding: '12px 20px', borderRadius: '10px',
            background: config.bg, border: `1px solid ${config.color}44`,
          }}>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' }}>JARVIS SAYS</div>
            <div style={{ fontSize: '14px', fontWeight: '800', color: config.color, fontFamily: THEME.fonts.hud }}>{config.action}</div>
          </div>
        </div>
      </div>

      {/* Upgrade hint */}
      {upgradeHint && (
        <div style={{ marginTop: '12px', padding: '8px 12px', background: 'rgba(0,212,255,0.06)', borderRadius: '8px', fontSize: '12px', color: THEME.colors.blue }}>
          💡 To upgrade: {upgradeHint}
        </div>
      )}
    </div>
  );
}

// ── Factor Toggle Row ───────────────────────────────────────────────────
function FactorRow({ factorKey, factor, checked, autoDetected, onChange }) {
  return (
    <div
      onClick={() => onChange(factorKey, !checked)}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '9px 12px', borderRadius: '8px', cursor: 'pointer',
        background: checked ? (factor.group === 'FIB' && factorKey === 'sir_macro_zone'
          ? 'rgba(0,255,136,0.08)' : 'rgba(255,255,255,0.04)')
          : 'transparent',
        border: checked ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
        transition: 'all 0.15s',
        marginBottom: '2px',
      }}
    >
      {/* Checkbox */}
      <div style={{
        width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
        background: checked ? THEME.colors.green : 'transparent',
        border: `2px solid ${checked ? THEME.colors.green : 'rgba(255,255,255,0.2)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px',
      }}>
        {checked && '✓'}
      </div>

      {/* Icon + label */}
      <span style={{ fontSize: '15px', flexShrink: 0 }}>{factor.icon}</span>
      <span style={{ flex: 1, fontSize: '13px', color: checked ? THEME.colors.text : THEME.colors.textMuted }}>
        {factor.label}
      </span>

      {/* Weight */}
      <span style={S.badge(checked ? '#000' : THEME.colors.textMuted, checked ? THEME.colors.green : 'rgba(255,255,255,0.05)')}>
        +{factor.weight.toFixed(1)}
      </span>

      {/* Auto badge */}
      {autoDetected && (
        <span style={S.badge(THEME.colors.blue, 'rgba(0,212,255,0.1)')}>AUTO</span>
      )}

      {/* Sir's special badge */}
      {factorKey === 'sir_macro_zone' && (
        <span style={S.badge('#000', THEME.colors.gold)}>SIR'S LEVEL</span>
      )}
    </div>
  );
}

// ── Matched / Missing summary ───────────────────────────────────────────
function MatchedMissing({ result }) {
  if (!result || result.factorCount === 0) return null;
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {result.matched.length > 0 && (
        <div style={{ ...S.card('rgba(0,255,136,0.15)'), flex: 1, minWidth: '200px' }}>
          <div style={S.sectionTitle}>✅ CONFIRMED ({result.matched.length})</div>
          {result.matched.map(f => (
            <div key={f.key} style={{ fontSize: '12px', color: THEME.colors.green, marginBottom: '4px' }}>
              {f.icon} {f.label.split('(')[0].trim()}
            </div>
          ))}
        </div>
      )}
      {result.topMissing.length > 0 && (
        <div style={{ ...S.card('rgba(255,51,51,0.10)'), flex: 1, minWidth: '200px' }}>
          <div style={S.sectionTitle}>❌ TOP MISSING (add these to upgrade)</div>
          {result.topMissing.map(f => (
            <div key={f.key} style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>{f.icon}</span>
              <span>{f.label.split('(')[0].trim()}</span>
              <span style={S.badge(THEME.colors.red, 'rgba(255,51,51,0.1)')}>{f.weight.toFixed(1)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Trade Plan (auto-filled when A/A+) ─────────────────────────────────
function TradePlanSection({ grade, direction, entry, setEntry, sl, setSl, tp1, setTp1, tp2, setTp2 }) {
  if (!['A+','A'].includes(grade)) return null;

  const rr1 = entry && sl && tp1
    ? Math.abs(tp1 - entry) / Math.abs(sl - entry)
    : null;

  const gradeConfig = GRADE_CONFIG[grade];
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: THEME.colors.text, fontSize: '14px', padding: '8px 10px', outline: 'none', fontFamily: THEME.fonts.hud, flex: 1 };

  return (
    <div style={S.card(`${gradeConfig.color}44`)}>
      <div style={S.sectionTitle}>📐 TRADE PLAN — GRADE {grade} SETUP</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {['BUY', 'SELL'].map(d => (
          <button key={d} style={{
            ...S.btn(direction === d ? (d === 'BUY' ? 'green' : 'danger') : ''),
            minWidth: '80px',
          }} onClick={() => {}}>
            {d === 'BUY' ? '▲ BUY' : '▼ SELL'}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '120px' }}>
          <span style={{ fontSize: '10px', color: THEME.colors.textMuted, letterSpacing: '1px' }}>ENTRY</span>
          <input style={inputStyle} type="number" step="0.01" placeholder="4030.00" value={entry} onChange={e => setEntry(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '120px' }}>
          <span style={{ fontSize: '10px', color: THEME.colors.red, letterSpacing: '1px' }}>STOP LOSS</span>
          <input style={{ ...inputStyle, border: '1px solid rgba(255,51,51,0.3)' }} type="number" step="0.01" placeholder="4010.00" value={sl} onChange={e => setSl(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '120px' }}>
          <span style={{ fontSize: '10px', color: THEME.colors.green, letterSpacing: '1px' }}>TP1</span>
          <input style={{ ...inputStyle, border: '1px solid rgba(0,255,136,0.3)' }} type="number" step="0.01" placeholder="4070.00" value={tp1} onChange={e => setTp1(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '120px' }}>
          <span style={{ fontSize: '10px', color: THEME.colors.green, letterSpacing: '1px' }}>TP2</span>
          <input style={inputStyle} type="number" step="0.01" placeholder="4110.00" value={tp2} onChange={e => setTp2(e.target.value)} />
        </div>
      </div>
      {rr1 !== null && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px 16px' }}>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>R:R RATIO</div>
            <div style={{ fontSize: '22px', fontWeight: '900', fontFamily: THEME.fonts.hud, color: rr1 >= 2 ? THEME.colors.green : rr1 >= 1.5 ? THEME.colors.gold : THEME.colors.red }}>
              1:{rr1.toFixed(1)}
            </div>
          </div>
          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px 16px' }}>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>SL DISTANCE</div>
            <div style={{ fontSize: '22px', fontWeight: '900', fontFamily: THEME.fonts.hud, color: THEME.colors.gold }}>
              {Math.abs(sl - entry).toFixed(1)}
            </div>
          </div>
          {rr1 < 1.5 && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={S.badge(THEME.colors.red, 'rgba(255,51,51,0.1)')}>⚠️ R:R below 1.5 — adjust TP or SL</span>
            </div>
          )}
          {rr1 >= 2 && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={S.badge('#000', THEME.colors.green)}>✅ Excellent R:R</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Context panel ───────────────────────────────────────────────────────
function ContextPanel() {
  const ctx = getSetupContext();
  return (
    <div style={{ ...S.card('rgba(0,212,255,0.1)'), display: 'flex', gap: '12px', flexWrap: 'wrap', padding: '12px 14px' }}>
      <div style={{ flex: 1, minWidth: '200px' }}>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' }}>SESSION</div>
        <div style={{ fontSize: '14px', fontWeight: '700', color: ctx.session.quality === 'PEAK' || ctx.session.quality === 'HIGH' ? THEME.colors.green : THEME.colors.textMuted }}>
          {ctx.session.emoji} {ctx.session.name} — {ctx.session.quality}
        </div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '4px' }}>{ctx.sessionTip}</div>
      </div>
      <div style={{ flex: 1, minWidth: '160px' }}>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' }}>DAY</div>
        <div style={{ fontSize: '14px', fontWeight: '700', color: THEME.colors.gold }}>{ctx.day}</div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '4px' }}>{ctx.dayTip}</div>
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────
const GROUPS = [
  { key: 'ICT',     label: 'ICT / SMC',        color: THEME.colors.blue },
  { key: 'FIB',     label: 'Fibonacci',         color: THEME.colors.gold },
  { key: 'CONTEXT', label: 'Context & Session', color: THEME.colors.textMuted },
];

export default function ConfluenceScorer() {
  const livePrice = getCurrentPrice();
  const [factors, setFactors]     = useState(() => {
    const init = {};
    Object.keys(CONFLUENCE_FACTORS).forEach(k => { init[k] = false; });
    return init;
  });
  const [autoLoaded, setAutoLoaded] = useState({});
  const [direction, setDirection]   = useState('BUY');
  const [entry, setEntry]           = useState('');
  const [sl, setSl]                 = useState('');
  const [tp1, setTp1]               = useState('');
  const [tp2, setTp2]               = useState('');
  const [note, setNote]             = useState('');

  const result = useMemo(() => calculateConfluence(factors), [factors]);

  const handleAutoLoad = () => {
    const detected = autoLoadFromStorage(livePrice);
    setAutoLoaded(detected);
    setFactors(prev => ({ ...prev, ...detected }));
  };

  const handleReset = () => {
    const empty = {};
    Object.keys(CONFLUENCE_FACTORS).forEach(k => { empty[k] = false; });
    setFactors(empty);
    setAutoLoaded({});
  };

  const handleToggle = (key, val) => {
    setFactors(prev => ({ ...prev, [key]: val }));
  };

  // Auto-load on mount if live price available
  useEffect(() => {
    if (livePrice > 0) handleAutoLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={S.page}>
      <div style={S.title}>⚡ CONFLUENCE ENGINE</div>
      <div style={S.sub}>Score every setup. Only trade A or A+. Protect capital first.</div>

      {/* Context */}
      <ContextPanel />

      {/* Controls */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <button style={S.btn('primary')} onClick={handleAutoLoad}>
          ⚡ Auto-Load from ICT Storage
        </button>
        <button style={S.btn()} onClick={handleReset}>Reset All</button>
        <div style={{ flex: 1 }} />
        {livePrice > 0 && (
          <span style={{ ...S.badge(THEME.colors.blue, 'rgba(0,212,255,0.1)'), alignSelf: 'center', fontSize: '12px', padding: '4px 12px' }}>
            XAU/USD {livePrice.toFixed(2)}
          </span>
        )}
        <span style={{ ...S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)'), alignSelf: 'center', fontSize: '12px', padding: '4px 12px' }}>
          {result.factorCount}/{result.totalFactors} factors
        </span>
      </div>

      {/* Grade Result */}
      <GradeCard result={result} />

      {/* Factor Groups */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {GROUPS.map(group => {
          const groupFactors = Object.entries(CONFLUENCE_FACTORS).filter(([, f]) => f.group === group.key);
          const groupChecked = groupFactors.filter(([k]) => factors[k]).length;
          return (
            <div key={group.key} style={{ ...S.card(`${group.color}22`), flex: 1, minWidth: '260px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: group.color, fontFamily: THEME.fonts.hud }}>
                  {group.label}
                </div>
                <span style={S.badge(group.color, `${group.color}15`)}>{groupChecked}/{groupFactors.length}</span>
              </div>
              {groupFactors.map(([key, factor]) => (
                <FactorRow
                  key={key}
                  factorKey={key}
                  factor={factor}
                  checked={factors[key]}
                  autoDetected={!!autoLoaded[key]}
                  onChange={handleToggle}
                />
              ))}
            </div>
          );
        })}
      </div>

      {/* Matched / Missing */}
      <MatchedMissing result={result} />

      {/* Trade Plan (only for A/A+) */}
      <TradePlanSection
        grade={result.grade}
        direction={direction} setDirection={setDirection}
        entry={parseFloat(entry)} setEntry={setEntry}
        sl={parseFloat(sl)} setSl={setSl}
        tp1={parseFloat(tp1)} setTp1={setTp1}
        tp2={parseFloat(tp2)} setTp2={setTp2}
      />

      {/* Notes */}
      <div style={S.card()}>
        <div style={S.sectionTitle}>SETUP NOTES</div>
        <textarea
          style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', color: THEME.colors.text, fontSize: '13px', padding: '10px', outline: 'none', fontFamily: THEME.fonts.body, resize: 'vertical', minHeight: '70px', boxSizing: 'border-box' }}
          placeholder="Note your reasoning here — e.g. 'H4 OB at 4024, London open, SSL swept at 4012, D1 bullish structure'"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      </div>

      {/* Grade reference */}
      <div style={S.card('rgba(255,215,0,0.08)')}>
        <div style={S.sectionTitle}>GRADE REFERENCE</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {Object.entries(GRADE_CONFIG).map(([grade, cfg]) => (
            <div key={grade} style={{ background: cfg.bg, border: `1px solid ${cfg.color}44`, borderRadius: '8px', padding: '8px 12px', minWidth: '140px', flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: '900', color: cfg.color, fontFamily: THEME.fonts.hud }}>{cfg.emoji} {grade}</div>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '2px' }}>{cfg.action}</div>
              <div style={{ fontSize: '11px', color: cfg.color, marginTop: '2px' }}>{cfg.winRate} win rate</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

